import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import EliteBadge from "@/components/EliteBadge";
import { WaxSeal } from "@/components/luxury";
import { thumbUrl } from "@/lib/utils-app";
import { UsersFourIcon as UsersFour, DogIcon as Dog, MagnifyingGlassIcon as MagnifyingGlass, CrownIcon as Crown } from "@phosphor-icons/react";

export default function PublicKennels() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [rows, setRows] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = q ? `/public/kennels?q=${encodeURIComponent(q)}` : "/public/kennels";
    api.get(url)
      .then((r) => {
        setRows(r.data?.kennels || []);
        setFeatured(r.data?.featured || null);
      })
      .catch(() => { setRows([]); setFeatured(null); })
      .finally(() => setLoading(false));
  }, [q]);

  return (
    <PublicShell>
      <div className="mb-8">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">Directory</p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">Browse all kennels</h1>
        <p className="text-[#6B6B6B] mt-2 max-w-2xl">
          Discover registered breeders, browse their dogs, and reach out directly. Every kennel has a dedicated public profile.
        </p>
      </div>

      <div className="bg-white border border-[#EAEAEA] rounded-xl p-3 mb-6 max-w-xl flex items-center gap-2">
        <MagnifyingGlass size={18} className="text-[#6B6B6B] ml-2" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setParams(e.target.value ? { q: e.target.value } : {}); }}
          placeholder="Search by name, kennel, location…"
          className="flex-1 outline-none bg-transparent text-sm py-1.5"
          data-testid="kennels-search-input"
        />
      </div>

      {loading ? (
        <div className="text-[#6B6B6B]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-12 text-center">
          <UsersFour size={40} weight="duotone" className="text-[#6B6B6B]/40 mx-auto mb-3" />
          <p className="text-[#6B6B6B]">No kennels match.</p>
        </div>
      ) : (
        <>
          {/* Featured kennel rotation — picks one Elite kennel deterministically per day.
              Server returns it in `featured` so the FE just renders the card. */}
          {!q && featured && (
            <Link
              to={`/k/${featured.slug || featured.id}`}
              className="block mb-8 group"
              data-testid="featured-kennel-card"
            >
              <div
                className="relative overflow-hidden rounded-2xl border border-[#C9A227]/40 bg-[#0E0E0E] text-white p-6 md:p-8 transition-shadow group-hover:shadow-xl"
                style={{ minHeight: 180 }}
              >
                {featured.hero_video_url ? (
                  <video
                    src={featured.hero_video_url}
                    poster={featured.hero_video_poster_url || undefined}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover opacity-40"
                    aria-hidden
                  />
                ) : null}
                <div
                  aria-hidden
                  className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#C9A227]/30 blur-3xl"
                />
                <div className="relative">
                  <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-light mb-2">
                    ★ Featured kennel · Today
                  </div>
                  <h2 className="font-spectral text-3xl md:text-4xl tracking-tight">
                    {featured.kennel_name || featured.name}
                  </h2>
                  {featured.location ? (
                    <p className="text-sm text-white/70 mt-1">{featured.location}</p>
                  ) : null}
                  {featured.bio ? (
                    <p className="mt-3 text-white/80 max-w-2xl line-clamp-2">{featured.bio}</p>
                  ) : null}
                  <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
                    {featured.elite_verified && (
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] bg-gold/90 text-ink rounded-full px-3 py-1">
                        Elite Verified
                      </span>
                    )}
                    <span className="text-white/70">
                      {featured.dog_count} dog{featured.dog_count === 1 ? "" : "s"} on registry
                    </span>
                    {(featured.award_case || []).length > 0 && (
                      <span className="text-white/70">
                        · {featured.award_case.length} award
                        {featured.award_case.length === 1 ? "" : "s"}
                      </span>
                    )}
                    <span className="ml-auto text-gold-light font-bold uppercase tracking-wider">
                      View kennel →
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          )}
          {/* Elite Verified hero — only visible when at least one kennel
              is on the prestige tier and we're not narrowing search. */}
          {!q && rows.some((r) => r.elite_verified) && (
            <div className="bg-gradient-to-br from-[#0E0E0E] via-[#1a1a1a] to-[#0E0E0E] rounded-2xl p-6 mb-8" data-testid="elite-kennel-hero">
              <div className="flex items-center gap-2 mb-4">
                <Crown size={20} weight="fill" className="text-gold" />
                <h2 className="font-spectral text-2xl tracking-tight text-white">Elite Verified Breeders</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {rows.filter((r) => r.elite_verified).slice(0, 6).map((b) => (
                  <Link
                    key={b.id}
                    to={`/k/${b.slug || b.id}`}
                    className="bg-white/5 hover:bg-white/10 border border-[#C9A227]/30 rounded-xl p-4 transition"
                    data-testid={`elite-kennel-card-${b.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-spectral text-lg text-white truncate flex-1">{b.kennel_name || b.name}</h3>
                      <EliteBadge elite size="sm" showLabel={false} />
                    </div>
                    {b.location && <p className="text-xs text-white/60">{b.location}</p>}
                    <div className="text-xs text-white/40 mt-2 flex items-center"><Dog size={12} className="mr-1" /> {b.dog_count} dogs</div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rows.map((b) => {
              const isVerified = b.verified || b.is_verified || b.elite_verified || b.seller_plan === "verified";
              const isUnclaimed = !!b.unclaimed;
              return (
              <Link
                key={b.id}
                to={`/k/${b.slug || b.id}`}
                className={`group rounded-xl p-5 transition-all duration-500 ${
                  isUnclaimed
                    ? "bg-[#FAFAF8] border border-dashed border-[#DDD8CC] opacity-75 hover:opacity-100"
                    : "bg-white border border-[#EAEAEA] hover:border-gold/55 hover:-translate-y-0.5 shadow-luxury-light hover:shadow-gold-glow"
                }`}
                data-testid={`kennel-card-${b.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="relative flex-shrink-0">
                    <div className="h-12 w-12 rounded-full overflow-hidden bg-cream ring-1 ring-[#E2DAC2] flex items-center justify-center">
                      {b.photo_url || b.logo_url ? (
                        <img src={thumbUrl(b.photo_url || b.logo_url)} alt={b.kennel_name || b.name} className="w-full h-full object-cover" data-testid={`kennel-logo-${b.id}`} />
                      ) : (
                        <UsersFour size={22} weight="duotone" className="text-ink" />
                      )}
                    </div>
                    {isVerified && <WaxSeal size={20} title="Verified kennel" className="absolute -bottom-0.5 -right-1" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h3 className="font-spectral text-xl tracking-tight group-hover:text-crimson transition-colors">{b.kennel_name || b.name}</h3>
                      {isUnclaimed ? (
                        <span
                          className="text-[9px] font-bold uppercase tracking-[0.18em] bg-[#EFEDE6] text-[#8A8477] border border-[#DDD8CC] rounded-full px-2 py-0.5"
                          data-testid={`kennel-unclaimed-badge-${b.id}`}
                          title="No linked member account — added by a member for pedigree records"
                        >
                          Unclaimed
                        </span>
                      ) : (
                        <EliteBadge elite={b.elite_verified} showLabel={false} />
                      )}
                    </div>
                    {b.location && <p className="text-xs text-[#6B6B6B] mt-0.5">{b.location}</p>}
                  </div>
                </div>
                <div className="gold-hairline mt-3" />
                {isUnclaimed ? (
                  <p className="text-xs text-[#8A8477] mt-3 italic leading-relaxed">
                    This kennel hasn't joined Armstrong K9 Registry yet.
                  </p>
                ) : (
                  <>
                    {b.bio && <p className="text-sm text-[#6B6B6B] mt-3 line-clamp-2 leading-relaxed">{b.bio}</p>}
                    <div className="mt-4 flex items-center text-xs text-[#6B6B6B] font-tabular">
                      <Dog size={14} className="mr-1" /> {b.dog_count} dog{b.dog_count !== 1 ? "s" : ""}
                    </div>
                  </>
                )}
              </Link>
            );})}
          </div>
        </>
      )}
    </PublicShell>
  );
}
